// Shared logic for the two pass editors (NewPass + EditPass). These flows were
// duplicated byte-for-byte between the pages; keep the single copy here so a
// change lands in one place instead of drifting between the two.

import logo from "../../assets/logo.png";
import { CROP_TYPES } from "../../components/ImageCropModal";
import {
  validateMilestones,
  usesMilestoneRewards,
  finalMilestoneIndex,
} from "../../utils/passUtils";

// Stamp icon shown in previews and the crop modal: the merchant's custom icon,
// falling back to the Loyaltify logo placeholder.
export const stampIconSource = (formData) => formData.stampIcon || logo;

// Title for the shared ImageCropModal, keyed by crop type.
export const getCropModalTitle = (type, t) => {
  switch (type) {
    case CROP_TYPES.BACKGROUND:
      return t("passes.form.background.crop.title");
    case CROP_TYPES.LOGO:
      return t("passes.form.logo.crop.title");
    case CROP_TYPES.MILESTONE:
      return t("passes.form.milestones.iconModal.cropTitle");
    default:
      return t("passes.form.stampIcon.crop.title");
  }
};

// Names a milestone row the way the builder heads it on screen, so the merchant
// can map the message straight onto a card they can see.
const milestoneRowName = (index, finalIndex, t) =>
  index === finalIndex
    ? t("passes.form.journeyBuilder.finalReward")
    : t("passes.form.journeyBuilder.milestone", { number: index + 1 });

// One line per problem, e.g. "Milestone 2: Enter a reward name". Composed from
// the same strings the inline errors use, so the notification and the red field
// always say the same thing and no new copy needs translating.
//
// Only i18n output is interpolated here — never the merchant's own reward text —
// because AlertNotification renders this through dangerouslySetInnerHTML.
const milestoneErrorMessage = (milestones, errors, t) => {
  const finalIndex = finalMilestoneIndex(milestones);

  const lines = Object.keys(errors)
    .map(Number)
    .sort((a, b) => a - b)
    .flatMap((index) => {
      const rowErrors = errors[index];
      const name = milestoneRowName(index, finalIndex, t);

      return [
        rowErrors.label && t("passes.form.milestones.labelRequired"),
        rowErrors.position &&
          t(
            rowErrors.position === "duplicate"
              ? "passes.form.milestones.positionDuplicate"
              : "passes.form.milestones.positionInvalid",
          ),
      ]
        .filter(Boolean)
        .map((problem) => `<li>${name}: ${problem}</li>`);
    });

  return `<ul class="list-disc list-inside">${lines.join("")}</ul>`;
};

// Milestone cards block save when a milestone is invalid. Returns true when the
// save may proceed; otherwise raises a notification naming every offending row
// and returns false. A no-op (returns true) for cards without milestone rewards.
//
// Takes showNotification rather than calling alert(): a native dialog blocks the
// page, is styled nothing like the rest of the app, and — being one generic
// sentence — left the merchant to hunt for the empty field themselves.
export const validateMilestonesForSave = (formData, t, showNotification) => {
  if (!usesMilestoneRewards(formData)) return true;

  const milestones = formData.milestones || [];
  const { errors, isValid } = validateMilestones(milestones);
  if (isValid) return true;

  showNotification(
    t("passes.form.milestones.validationError"),
    milestoneErrorMessage(milestones, errors, t),
    "error",
  );
  return false;
};

// Card types whose editor shows the tier block (target select + threshold).
// A coupon also carries a target, but its threshold is seeded and never shown,
// so a message pointing at that field would name something the merchant
// cannot see; the backend validation still covers it.
const TIERED_TYPES = ["cashback", "discount", "stamp"];

// A next card tier with no threshold behind it. The upgrade campaign is built
// from target_pass_id plus target_amount, and the backend rejects the pair when
// the amount is blank — but a request that is going to fail should not be sent,
// and the merchant should see which field is wrong, not a toast in the corner.
export const tierThresholdMissing = (formData) => {
  if (!TIERED_TYPES.includes(formData.type)) return false;

  const target = formData.targetPassId;
  if (!target || target === "none") return false;
  return String(formData.targetAmount ?? "").trim() === "";
};

// Blocks save when a next tier is selected with no threshold. Returns true when
// the save may proceed; otherwise raises a notification and returns false, the
// same contract as validateMilestonesForSave so the pages chain them.
export const validateTierForSave = (formData, t, showNotification) => {
  if (!tierThresholdMissing(formData)) return true;

  showNotification(
    t("passes.form.tier.validationError"),
    t("passes.form.tier.targetAmountRequired"),
    "error",
  );
  return false;
};

// Which of a coupon's reward fields are blank. They become the name and
// description of the campaigns the backend builds for the coupon, which it
// requires — a blank one used to fail the create with an empty error list and
// be saved straight into the card on update. Returns {} for a valid coupon
// and for every other card type.
export const couponRewardErrors = (formData) => {
  if (formData.type !== "coupon") return {};

  const reward = formData.campaign || {};
  const errors = {};
  if (String(reward.label ?? "").trim() === "") errors.label = true;
  if (String(reward.value ?? "").trim() === "") errors.value = true;
  return errors;
};

// Blocks save when a coupon's reward label or value is blank. Same contract as
// validateMilestonesForSave / validateTierForSave.
export const validateCouponForSave = (formData, t, showNotification) => {
  const errors = couponRewardErrors(formData);
  if (!errors.label && !errors.value) return true;

  const lines = [
    errors.label && t("passes.form.campaign.labelRequired"),
    errors.value && t("passes.form.campaign.valueRequired"),
  ]
    .filter(Boolean)
    .map((line) => `<li>${line}</li>`);

  showNotification(
    t("passes.form.campaign.validationError"),
    `<ul class="list-disc list-inside">${lines.join("")}</ul>`,
    "error",
  );
  return false;
};

// Reads a picked milestone-icon File and opens the shared crop modal for it,
// tagging the crop with the milestone's stable id (so a reorder mid-crop can't
// land the icon on the wrong milestone).
export const openMilestoneIconCrop = (file, milestoneId, setCropModalState) => {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    setCropModalState({
      isOpen: true,
      type: CROP_TYPES.MILESTONE,
      imageUrl: reader.result,
      milestoneId,
    });
  };
  reader.readAsDataURL(file);
};
